// This component is the draggable table column are then dropped into dropzones

import React, { useState } from 'react';
import { useDrag } from 'react-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { MdOutlineKeyboardArrowUp, MdOutlineKeyboardArrowDown } from 'react-icons/md';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import { AccessTime, CalendarToday, WarningAmber } from '@mui/icons-material';
import { createSvgIcon, Tooltip } from '@mui/material';
import { fontSize, palette } from '../..';
import { AcceptRejectDialog } from '../CommonFunctions/DialogComponents';
import { Area } from '../CommonFunctions/aliases';
import { ICurrentCalculationSession } from '../../redux/Calculations/CurrentCalculationSessionInterface';
import { AiOutlineFunction } from 'react-icons/ai';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const BooleanIcon = createSvgIcon(
  <svg
    viewBox="0 0 76 76"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    version="1.1"
    baseProfile="full"
    enable-background="new 0 0 76.00 76.00"
    xmlSpace="preserve"
    fill="rgb(72,72,72)"
  >
    <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
    <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g>
    <g id="SVGRepo_iconCarrier">
      <path
        fill="rgb(72,72,72)"
        fill-opacity="1"
        stroke-linejoin="round"
        d="M 36,23L 30,23L 30,40L 25,40L 25,23L 19,23L 19,19L 36,19L 36,23 Z M 57,40L 50,40L 50,45L 56,45L 56,49L 50,49L 50,57L 45,57L 45,36L 57,36L 57,40 Z M 44,19L 48.5,19L 32.5,57L 28,57L 44,19 Z "
      ></path>
    </g>
  </svg>,
  'Boolean',
);

interface BoxProps {
  name: string;
  type: string;
  fieldData: any;
  colsOnly: boolean;
  warning?: any;
  isSavedCalculation?: boolean | ICurrentCalculationSession;
  isPresentInAxes?: boolean;
  isPresentInFilter?: boolean;
  handleEditButton?: () => void;
  handleDeleteButton?: () => void;
  handleAddParameter?: () => void;
  allSavedCalculations?: ICurrentCalculationSession[];
  deleteIfPresentInAxes?: (propKey: string, binIndex: number, fieldIndex: number) => void;
  deleteIfPresentInFilter?: (groupKey: string, filterIndex: number) => void;
  informationForPropDeletion?: Record<string, any[]>;
  informationForFilterDeletion?: Record<string, any[]>;
}

export const Box = ({
  name,
  type,
  fieldData,
  colsOnly,
  isSavedCalculation,
  warning,
  isPresentInAxes,
  isPresentInFilter,
  handleEditButton = () => {},
  handleDeleteButton = () => {},
  handleAddParameter = () => {},
  allSavedCalculations = [],
  deleteIfPresentInAxes = () => {},
  deleteIfPresentInFilter = () => {},
  informationForPropDeletion,
  informationForFilterDeletion,
}: BoxProps) => {
  let boxUUID: string | null; // boxUUID is id of the saved calculation can be row level or aggregated
  let isOverrided = false; // indicates if a calculation is overrided or not
  let isAggregated = false; // indicated a calculation is rowlevel or visual level aggregation

  if (isSavedCalculation) {
    boxUUID =
      allSavedCalculations.find(
        (calc) =>
          calc.calculationInfo.calculatedFieldName?.toLowerCase().replace(/[^a-z0-9]/g, '') ===
          fieldData.fieldname.toLowerCase().replace(/[^a-z0-9]/g, ''),
      )?.uuid || null;
    if (boxUUID) {
      const calc = allSavedCalculations.find((calc) => calc.uuid === boxUUID);
      if (calc) {
        isAggregated = calc.isAggregated ?? false;
        if (calc.calculationInfo.overrideCount >= 1) isOverrided = true;
      }
    }
  } else {
    boxUUID = null;
  }

  const [opacity, drag] = useDrag<any>({
    type: type ?? 'card',
    canDrag: () => {
      const valid = !!fieldData?.dataType || !!fieldData?.columns?.dataType;
      if (!valid) setShowUndefinedDataTypeWarning(true);
      return valid;
    },
    item: {
      name,
      type,
      fieldData: { ...fieldData, hasOverridedFlow: isOverrided },
      bIndex: 99,
      boxUUID,
      dragFrom: Area.TABLE,
      isOverrided,
      isAggregated,
    },
    collect: (monitor: any) => ({
      opacity: monitor.isDragging() ? 0.4 : 1,
    }),
  });

  const [isDivHoverActive, setIsDivHoverActive] = React.useState(false);
  const [arrowDirection, setArrowDirection] = React.useState<'down' | 'up'>('down');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showUndefinedDataTypeWarning, setShowUndefinedDataTypeWarning] = React.useState(false);
  const [tooltipOpen, setTooltipOpen] = React.useState(false);

  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setTooltipOpen(false);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setArrowDirection('down');
  };

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const getDataTypeIcon = (dataType?: string) => {
    if (!dataType) {
      return <QuestionMarkIcon style={{ height: '11px', width: '12px' }} />;
    }
    switch (dataType.toLowerCase()) {
      case 'integer':
      case 'number':
        return (
          <img src="/Integer_Icon.svg" alt="Integer" style={{ height: '12px', width: '12px' }} />
        );
      case 'text':
        return <img src="/Text_Icon.svg" alt="Text" style={{ height: '12px', width: '12px' }} />;
      case 'timestamp':
        return <AccessTime style={{ height: '12px', width: '12px' }} />;
      case 'date':
        return <CalendarToday style={{ height: '10px', width: '10px', marginBottom: '2px' }} />;
      case 'decimal':
        return (
          <img src="/Decimal_Icon.svg" alt="Decimal" style={{ height: '12px', width: '12px' }} />
        );
      case 'boolean':
        return <BooleanIcon style={{ height: '12px', width: '12px' }} />;
      default:
        return <QuestionMarkIcon style={{ height: '11px', width: '12px' }} />;
    }
  };
  const handleDropDownMenuClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (arrowDirection === 'down') setArrowDirection('up');
    else setArrowDirection('down');

    handleClick(e);
  };

  const savedCalculationMenu = isSavedCalculation && (
    <div
      style={{
        visibility: `${isDivHoverActive ? 'visible' : 'hidden'}`,
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
      }}
    >
      <button
        id="basic-button"
        aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleDropDownMenuClick}
        style={{
          height: '100%',
          minWidth: '1.5rem',
          maxWidth: '1.5rem',
          backgroundColor: 'transparent',
          border: 'none',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginLeft: '-0.3rem',
          marginRight: '-0.3rem',
        }}
      >
        {arrowDirection === 'down' ? (
          <MdOutlineKeyboardArrowDown style={{ color: palette.primary.contrastText }} />
        ) : (
          <MdOutlineKeyboardArrowUp style={{ color: palette.primary.contrastText }} />
        )}
      </button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => {
          setIsDivHoverActive(false);
          handleClose();
        }}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {type === 'parameter' && (
          <MenuItem
            sx={{ color: palette.primary.contrastText, fontSize: '0.75rem' }}
            dense
            onClick={() => {
              setIsDivHoverActive(false);
              handleAddParameter();
              handleClose();
              setArrowDirection('down');
            }}
          >
            Show
          </MenuItem>
        )}
        <MenuItem
          sx={{ color: palette.primary.contrastText, fontSize: '0.75rem' }}
          dense
          onClick={() => {
            setIsDivHoverActive(false);
            handleEditButton();
            handleClose();
            setArrowDirection('down');
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          sx={{ color: palette.primary.contrastText, fontSize: fontSize.medium }}
          dense
          onClick={() => {
            if (isPresentInAxes) {
              setIsDeleteDialogOpen(true);
            } else {
              setIsDivHoverActive(false);
              handleDeleteButton();
              handleClose();
            }
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </div>
  );

  return (
    <div style={{ display: 'flex', width: '100%' }}>
      <Tooltip
        title={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            <span>{name}</span>

            {fieldData?.dataType && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                {/* Handle both SVG and IMG icons */}
                {(() => {
                  const icon = getDataTypeIcon(fieldData.dataType);

                  if (!icon) return null;

                  // If it's an <img>, add filter to make it white
                  if ((icon as any).type === 'img') {
                    return React.cloneElement(icon as React.ReactElement, {
                      style: {
                        height: '12px',
                        width: '12px',
                        filter: 'brightness(0) invert(1)', // makes image white
                      },
                    });
                  }

                  // For SVG icons, just set color
                  return React.cloneElement(icon as React.ReactElement, {
                    style: { height: '12px', width: '12px', color: 'white' },
                  });
                })()}

                <span style={{ fontSize: '12px' }}>{fieldData.dataType}</span>
              </div>
            )}
          </div>
        }
        open={tooltipOpen}
        onOpen={() => !open && setTooltipOpen(true)}
        onClose={() => setTooltipOpen(false)}
        slotProps={{
          tooltip: {
            sx: {
              backgroundColor: '#6D6D6D',
              color: 'white',
              fontSize: '12px',
              padding: '4px 7px',
            },
          },
        }}
        PopperProps={
          !colsOnly
            ? {
                modifiers: [
                  {
                    name: 'offset',
                    options: {
                      offset: [55, 0],
                    },
                  },
                ],
              }
            : undefined
        }
      >
        <div
          onMouseEnter={() => setIsDivHoverActive(true)}
          onMouseLeave={() => setIsDivHoverActive(false)}
          ref={drag}
          style={{
            display: 'flex',
            flexDirection: !colsOnly ? 'column' : 'row',
            alignItems: colsOnly ? 'center' : '',
            justifyContent: colsOnly ? 'space-between' : 'normal',
            width: '100%',
            paddingInline: '0.2rem',
            borderRadius: '4px',
            boxSizing: 'border-box',
            ...((isAggregated || type === 'parameter') && {
              border: '1px solid #E0E0E0',
              backgroundColor: '#FFFFFF',
              marginBottom: '4px',
            }),
          }}
          className={colsOnly ? 'styleForColumnsOnly' : 'styleForTableHeader'}
        >
          {(isPresentInAxes || isPresentInFilter) && (
            <AcceptRejectDialog
              acceptFunction={() => {
                setIsDivHoverActive(false);
                handleDeleteButton();
                handleClose();

                // Delete from chartAxes
                for (const propKeyForThisDeleteInfo in informationForPropDeletion) {
                  informationForPropDeletion[propKeyForThisDeleteInfo].forEach((item) => {
                    const binIndexForThisPropKey = item.binIndex;
                    const fieldIndexForThisPropKey = item.fieldIndex;
                    deleteIfPresentInAxes(
                      propKeyForThisDeleteInfo,
                      binIndexForThisPropKey,
                      fieldIndexForThisPropKey,
                    );
                    setIsDeleteDialogOpen(false);
                  });
                }

                // Delete from chartFilterGroup
                for (const groupKeyForThisDeleteInfo in informationForFilterDeletion) {
                  informationForFilterDeletion[groupKeyForThisDeleteInfo].forEach((item) => {
                    const filterIndexForThisGroupKey = item.filterIndex;
                    deleteIfPresentInFilter(groupKeyForThisDeleteInfo, filterIndexForThisGroupKey);
                  });
                }
              }}
              rejectFunction={() => {
                setIsDeleteDialogOpen(false);
              }}
              messages={[
                {
                  text: `This calculation is used in the axes or filters. Do you still want to delete it?`,
                },
              ]}
              open={isDeleteDialogOpen}
              closeFunction={() => {
                setIsDeleteDialogOpen(false);
              }}
              heading=""
              acceptText="Delete"
              rejectText="Cancel"
              varient="static"
            />
          )}
          {/* colsOnly is true when its  aggregated calculation/ parameters or column view from display table  */}
          {colsOnly ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              {/* MOVED: Drag indicator SECOND */}
              <DragIndicatorIcon
                style={{
                  color: 'rgb(125,123,123)',
                }}
                fontSize="small"
              />

              {/* MOVED: Name and datatype section THIRD (last) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  flex: 1,
                  gap: '4px',
                  textAlign: 'start',
                  paddingLeft: '3px',
                }}
              >
                <span
                  className="boxText"
                  style={{
                    fontSize: '0.75rem',
                    fontFamily: 'Roboto-Bold',
                    color: 'rgb(125,123,123)',
                    maxWidth: isSavedCalculation ? '90px' : '140px',
                    overflow: 'hidden',
                    // maxWidth: isSavedCalculation ? '105px' : '110px',
                    width: isSavedCalculation ? '105px' : '125px',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textAlign: 'start',
                  }}
                >
                  {name}
                </span>
                {warning && (
                  <Tooltip
                    arrow
                    placement="top"
                    title={
                      type === 'parameter'
                        ? 'The column used in the parameter is missing in the dataset'
                        : 'The column used in the calculation is missing in the dataset'
                    }
                    slotProps={{
                      tooltip: {
                        sx: {
                          fontSize: '12px',
                        },
                      },
                    }}
                  >
                    <WarningAmber
                      style={{
                        color: '#850000ff',
                        flexShrink: 0,
                        marginRight: colsOnly ? '-10px' : '0px',
                      }}
                      fontSize="small"
                    />
                  </Tooltip>
                )}
                <span
                  style={{
                    justifyContent: 'flex-end',
                    display: 'flex',
                    flex: 1,
                    alignItems: 'center',
                  }}
                >
                  {savedCalculationMenu}
                  {isSavedCalculation && type !== 'parameter' && !isAggregated && (
                    <AiOutlineFunction color={'rgb(125,123,123)'} size={12} />
                  )}
                  {fieldData?.dataType && getDataTypeIcon(fieldData.dataType)}
                  {type === 'parameter' &&
                    fieldData.columns?.dataType &&
                    getDataTypeIcon(fieldData.columns.dataType)}
                </span>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', width: '100%' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '2px',
                  transform: 'TranslateX(-5px)',
                }}
              >
                <DragIndicatorIcon style={{ color: 'rgb(125,123,123)' }} fontSize="large" />
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minWidth: 0,
                  transform: 'TranslateX(-6px)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-start',
                    gap: '0.2rem',
                    alignItems: 'center',
                    transform: 'TranslateX(-1px)',
                  }}
                >
                  {fieldData?.dataType && (
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'rgb(125,123,123)',
                        // marginRight: '-15px',
                      }}
                    >
                      {getDataTypeIcon(fieldData.dataType)}
                    </span>
                  )}

                  {isSavedCalculation && <AiOutlineFunction color={'rgb(125,123,123)'} size={12} />}
                  {isSavedCalculation && warning && (
                    <Tooltip
                      arrow
                      placement="top"
                      title={
                        'The field is missing from the dataset and cannot be used in the row level calculation'
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            fontSize: '12px',
                          },
                        },
                      }}
                    >
                      <WarningAmber
                        style={{
                          color: '#850000ff',
                        }}
                        fontSize="inherit"
                      />
                    </Tooltip>
                  )}
                  {savedCalculationMenu}
                </div>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span
                    className="boxText"
                    style={{
                      fontSize: '0.75rem',
                      fontFamily: 'Roboto-Bold',
                      color: 'rgb(125,123,123)',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                      maxWidth: isSavedCalculation ? '95px' : '120px',
                    }}
                  >
                    {name}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </Tooltip>
      <Snackbar
        open={showUndefinedDataTypeWarning}
        autoHideDuration={5000}
        onClose={() => setShowUndefinedDataTypeWarning(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MuiAlert severity="warning" elevation={6} variant="filled" sx={{ fontSize: '0.85rem' }}>
          Fields with unsupported datatypes can't be used.
        </MuiAlert>
      </Snackbar>
    </div>
  );
};
